import React from 'react';
import { FaCoins } from 'react-icons/fa';
const CoinDistributionCard = ({ quizData }) => {
    // Process and calculate coin distribution
    const coinStats = React.useMemo(() => {
        if (!quizData || quizData.length === 0) {
            return {
                totalCoins: 0,
                typeDistribution: [],
                maxCoins: 0,
                minCoins: 0,
                avgCoins: 0
            };
        }

        // Group coins by type
        const coinsByType = quizData.reduce((acc, item) => {
            const type = item.type?.trim().toLowerCase() || 'unknown';
            const coins = item.coins || 0;

            if (!acc[type]) {
                acc[type] = {
                    type,
                    totalCoins: 0,
                    count: 0,
                    maxCoins: -Infinity,
                    minCoins: Infinity
                };
            }

            // For admin type, treat as deduction
            if (type === 'admin') {
                acc[type].totalCoins -= Math.abs(coins);
            } else {
                acc[type].totalCoins += coins;
            }

            acc[type].count++;
            acc[type].maxCoins = Math.max(acc[type].maxCoins, coins);
            acc[type].minCoins = Math.min(acc[type].minCoins, coins);

            return acc;
        }, {});

        // Calculate overall stats
        const totalCoins = Object.values(coinsByType).reduce((sum, typeData) => sum + typeData.totalCoins, 0);

        // Convert to array and calculate averages
        const typeDistribution = Object.values(coinsByType).map(typeData => {
            const avgCoins = typeData.count > 0 ? typeData.totalCoins / typeData.count : 0;
            return {
                ...typeData,
                avgCoins: Math.round(avgCoins * 100) / 100,
                percentage: totalCoins !== 0 ? (typeData.totalCoins / totalCoins) * 100 : 0
            };
        });

        // Sort by total coins (absolute value)
        typeDistribution.sort((a, b) => Math.abs(b.totalCoins) - Math.abs(a.totalCoins));

        // Find overall max and min
        const allCoins = quizData.map(item => item.coins || 0);
        const maxCoins = Math.max(...allCoins);
        const minCoins = Math.min(...allCoins);
        const avgCoins = allCoins.length > 0 ? allCoins.reduce((a, b) => a + b, 0) / allCoins.length : 0;

        return {
            totalCoins,
            typeDistribution,
            maxCoins,
            minCoins,
            avgCoins: Math.round(avgCoins * 100) / 100
        };
    }, [quizData]);

    const colorMap = {
        'daily': '#5470c6',       // Blue
        'welcome': '#91cc75',     // Green
        'admin': '#fac858',       // Yellow/Orange
        'ad': '#ee6666',          // Red
        'readwin': '#73c0de',     // Light Blue
        'dailystreak': '#3ba272', // Dark Green
        'jackpot': '#0900ff',     // Blue
        'nonstop': "#f56a00",     // Orange
        'tapwin': '#9b59b6',      // Purple
        'wrongans': '#e74c3c',     // Dark Red
        'whois': '#f72585',     // Black
        'fsad': '#1abc9c',        // Teal
        'rwdad': '#e67e22',       // Carrot Orange
        'ntvad': '#8e44ad',       // Deep Purple
        'inhousad': '#2ecc71',   // Emerald Green
        'game': '#010402',   // black
        'notification': '#ff69b4' // Hot Pink 
    };


    // Format number with commas
    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num);
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                    <div className="bg-amber-500 p-2 rounded-lg shadow-md text-white mr-3">
                        <FaCoins size={20} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">Coin Distribution</h2>
                </div>
                <div className="text-sm text-gray-600">
                    <span className="font-semibold">{formatNumber(coinStats.totalCoins)}</span> total coins
                </div>
            </div>




            {/* Type Distribution */}
            <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">Distribution by Quiz Type</h3>

                {coinStats.typeDistribution.length > 0 ? (
                    <div className="space-y-2 mt-2">
                        {coinStats.typeDistribution.map((typeData) => {
                            const isNegative = typeData.totalCoins < 0;
                            const displayName = typeData.type.charAt(0).toUpperCase() + typeData.type.slice(1);
                            const color = colorMap[typeData.type] || '#888';

                            return (
                                <div key={typeData.type} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                    <div className="flex justify-between items-center mb-1">
                                        <div className="flex items-center">
                                            <span
                                                className="w-3 h-3 rounded-full mr-2"
                                                style={{ backgroundColor: color }}
                                            ></span>
                                            <span className="text-sm font-medium text-gray-700">{displayName}</span>
                                        </div>
                                        <span className={`text-sm font-bold ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                                            {isNegative ? '-' : '+'}{formatNumber(Math.abs(typeData.totalCoins))}
                                        </span>
                                    </div>

                                    {/* Progress bar */}
                                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                        <div
                                            className={`h-2 rounded-full ${isNegative ? 'bg-red-500' : 'bg-green-500'}`}
                                            style={{
                                                width: `${Math.min(Math.abs(typeData.percentage), 100)}%`,
                                                opacity: 0.7
                                            }}
                                        ></div>
                                    </div>

                                    {/* Details */}
                                    <div className="flex justify-between mt-1 text-xs text-gray-500">
                                        <span>Count: {typeData.count}</span>
                                        <span>Avg: {formatNumber(typeData.avgCoins)}/quiz</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center text-gray-500">
                        No coin data available
                    </div>
                )}
            </div>
        </div>
    );
};

export default CoinDistributionCard;